import React, { useState, useMemo, useCallback } from 'react'
import { DialogContent, MenuItem, Typography, Button } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import StyledDialog from '../../Common/Dialogs/StyledDialog'
import { MainButton } from '../../Button/MainButton'
import {
	formatNumsToDollars,
	petMeetsCourseCriteria,
	PROMO_API_ACTIONS,
	TRAINING,
	calculatePromoAmountToApply,
	NON_BREAKING_SPACE
} from '../../../utils'
import useSelectedFacility from '../../../hooks/useSelectedFacility'
import ClientAutocomplete from '../../Autocompletes/Client'
import StyledSelect from '../../Common/StyledSelect'
import StyledAlert from '../../Common/StyledAlert'
import CheckboxMenu from '../../Common/Menus/CheckboxMenu'
import PetAvatar from '../../Common/PetAvatar'
import { dangers, gray } from '../../../utils/colors'
import PetAlert from '../../Common/PetAlert'
import PromoOnlyEntryDialog from '../Promos/PromoOnlyEntryDialog'

const useStyles = makeStyles(() => ({
	dialogContent: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		padding: '0 1.5rem',
		gap: '1rem',
		flex: 1
	},
	rowWrapper: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem'
	},
	row: {
		display: 'flex',
		flexDirection: 'row',
		'& > div:nth-child(1)': {
			flex: 1
		},
		'& > div:nth-child(2)': {
			width: '35%'
		},
		gap: '1.5rem'
	},
	dialogActions: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		gap: '1rem',
		margin: '2rem 0 1rem 0',
		'& .MuiButton-outlinedSizeLarge': {
			height: 'unset'
		},
		justifySelf: 'flex-end'
	},
	abbrAdornment: {
		marginLeft: '10px',
		marginRight: 0,
		marginTop: '19px !important',
		'& > p': {
			fontSize: '1rem'
		}
	},
	summaryWrapper: {
		backgroundColor: gray[200],
		padding: '1.125rem',
		borderRadius: '0.375rem',
		'& h2': {
			fontSize: '1rem',
			color: gray[800]
		},
		display: 'flex',
		flexDirection: 'column',
		gap: '.5rem'
	},
	summaryTableWrapper: {
		flex: 1,
		display: 'flex',
		'& table': {
			width: '100%',
			color: gray[800],
			borderWidth: '1px',
			borderColor: gray[300],
			borderStyle: 'solid',
			borderLeftWidth: 0,
			borderRightWidth: 0,
			flex: 1,
			'& tr': {
				'& th': {
					fontWeight: 600,
					textAlign: 'left',
					fontSize: '0.75rem',
					textTransform: 'uppercase',
					padding: '.3rem 0'
				},
				'& td': {
					textAlign: 'left',
					fontSize: '0.875rem',
					padding: '.3rem 0'
				},
				'& th:first-child': {
					width: '55%'
				},
				'& td:first-child': {
					paddingRight: '2%'
				},
				'& th:last-child': {
					textAlign: 'right'
				},
				'& td:last-child': {
					textAlign: 'right'
				}
			}
		}
	},
	summaryTotalWrapper: {
		display: 'flex',
		fontSize: '1rem',
		'& div:first-child': {
			flex: 1
		}
	},
	petAvatarContainer: {
		display: 'flex',
		gap: '1rem',
		alignItems: 'center'
	}
}))

/**
 *
 * @param {{
  open: Boolean, 
	onClose: Function, 
	onCancelled: Function,
	title: String,
	currentEnrolledPetIds: Set,
	currentEnrolledHandlerIds: Set,
	trainingClass: Object
}} params
 * @returns
 */
const TrainingEnrollmentDialog = ({
	open,
	onSaved,
	onCancelled,
	title = 'Add Attendee',
	currentEnrolledPetIds = new Set(),
	currentEnrolledHandlerIds = new Set(),
	trainingClass
}) => {
	const classes = useStyles()

	const [editedEnrollment, setEditedEnrollment] = useState({})
	const { saveEnrollmentForClass, facility, loading } = useSelectedFacility()
	const [error, setError] = useState(null)
	const [promoEntryDialogOpen, setPromoEntryDialogOpen] = useState(false)
	const [appliedPromo, setAppliedPromo] = useState(null)
	const [availableCredit, setAvailableCredit] = useState(0)

	const handleResetDialog = () => {
		setEditedEnrollment({})
		setAppliedPromo(null)
		setError(null)
	}

	const handleOnCancelled = () => {
		if (onCancelled) {
			handleResetDialog()
			onCancelled()
		}
	}

	const handleOnSave = async () => {
		const accounts = [editedEnrollment.primary_handler._id]

		if (editedEnrollment.secondary_handler) {
			accounts.push(editedEnrollment.secondary_handler)
		}

		const enrollment = {
			training_class_id: trainingClass._id,
			pet_ids: editedEnrollment.pets.map((pet) => pet._id),
			household_id: editedEnrollment.primary_handler.household,
			accounts,
			applied_promo: appliedPromo
				? {
						promo_id: appliedPromo._id,
						service_target: TRAINING,
						household_id: editedEnrollment.primary_handler.household,
						training_course_abbreviation: trainingClass.training_course.abbreviation
				  }
				: null
		}

		const { success, errors } = await saveEnrollmentForClass({
			facility_id: facility?._id,
			enrollment
		})

		if (!success && errors && errors.length > 0) {
			const [error] = errors
			if (typeof error === 'string') {
				setError(error)
			} else if (typeof error === 'object' && error.message) {
				setError(error.message)
			} else {
				setError('An error occurred while saving the enrollment.')
			}
		}

		if (success && onSaved) {
			handleResetDialog()
			onSaved()
		}
	}

	const handleFieldUpdate = ({ field, targetValue }) => {
		const [mainField, subField] = field.split('.')
		setEditedEnrollment((prev) => {
			const revised = { ...prev }
			if (subField) {
				revised[mainField] = { ...revised[mainField], [subField]: targetValue }
			} else {
				revised[field] = targetValue
			}

			return revised
		})
	}

	const handleHouseholdSelected = (household) => {
		const {
			account,
			accounts,
			pets,
			credit = 0,
			giftcard_balance = 0
		} = household || { account: null, accounts: [], pets: [] }
		const otherAccounts = accounts.filter((acc) => acc._id !== account._id)
		setAvailableCredit(credit + giftcard_balance)
		setEditedEnrollment(() => {
			return {
				primary_handler: account,
				otherHouseholdAccounts: otherAccounts,
				availableHouseholdPets: pets
			}
		})
	}

	const handlePromoEntryDialogState = useCallback(() => {
		setPromoEntryDialogOpen((prev) => !prev)
	}, [setPromoEntryDialogOpen])

	const handleRemoveAppliedPromo = useCallback(() => {
		setAppliedPromo(null)
	}, [setAppliedPromo])

	const handleOnPromoValidationComplete = useCallback(
		(promo) => {
			setAppliedPromo(promo)
			handlePromoEntryDialogState()
		},

		[setAppliedPromo]
	)

	const primaryAlreadyEnrolled = useMemo(() => {
		return currentEnrolledHandlerIds.has(editedEnrollment.primary_handler?._id)
	}, [editedEnrollment.primary_handler, currentEnrolledHandlerIds])

	const secondaryAlreadyEnrolled = useMemo(() => {
		return editedEnrollment.secondary_handler
			? currentEnrolledHandlerIds.has(editedEnrollment.secondary_handler)
			: false
	}, [editedEnrollment.secondary_handler, currentEnrolledHandlerIds])

	const canEnrollMorePets = useMemo(() => {
		if (primaryAlreadyEnrolled && !editedEnrollment.secondary_handler) {
			return false
		}

		if (secondaryAlreadyEnrolled) {
			return false
		}

		return true
	}, [editedEnrollment, primaryAlreadyEnrolled, secondaryAlreadyEnrolled])

	const petOptions = useMemo(() => {
		if (!editedEnrollment.availableHouseholdPets) {
			return []
		}

		const options = editedEnrollment.availableHouseholdPets
			.filter((option) => ['Dog', 'dog'].includes(option.animal_type))
			.map((option) => {
				const { training_course } = trainingClass || {}
				const { name, image_url, profile_image_file_name, _id } = option
				const { meets_requirements, issues = [] } = petMeetsCourseCriteria({
					pet: option,
					course: training_course,
					startDate: trainingClass.startDate,
					endDate: trainingClass.endDate
				})

				const disabled = !meets_requirements || currentEnrolledPetIds.has(_id) || !canEnrollMorePets

				return {
					component: (
						<div className={classes.petAvatarContainer}>
							<PetAvatar petName={name} imageUrl={image_url || profile_image_file_name} />
							{issues.length > 0 ? <PetAlert issues={issues} /> : null}
						</div>
					),
					value: _id,
					disabled
				}
			})
		return options
	}, [editedEnrollment.availableHouseholdPets, trainingClass, currentEnrolledPetIds, canEnrollMorePets])

	const canSave = useMemo(() => {
		const { pets = [] } = editedEnrollment
		return canEnrollMorePets && pets.length > 0
	}, [editedEnrollment, canEnrollMorePets])

	const numberOfSessions = useMemo(() => {
		return trainingClass.number_of_sessions * editedEnrollment.pets?.length || 0
	}, [trainingClass, editedEnrollment.pets])

	const totalCost = useMemo(() => {
		if (!trainingClass || !trainingClass.price_per_session) {
			return 0
		}
		return trainingClass.price_per_session * numberOfSessions
	}, [numberOfSessions, trainingClass])

	const canAddPromo = useMemo(() => {
		const { pets = [] } = editedEnrollment
		return pets.length > 0
	}, [editedEnrollment])

	const promoAmountToApply = useMemo(() => {
		if (!appliedPromo) {
			return 0
		}

		return calculatePromoAmountToApply({
			promo: appliedPromo,
			total: totalCost,
			service_target: TRAINING
		})
	}, [appliedPromo])

	const totalCostWithPromo = useMemo(() => {
		return totalCost + promoAmountToApply
	}, [promoAmountToApply, totalCost])

	const householdCreditToApply = useMemo(() => {
		const absoluteCredit = Math.abs(availableCredit)

		if (absoluteCredit <= 0) {
			return 0
		}

		let total = totalCostWithPromo

		if (total <= 0) {
			return 0
		}

		let amount = total - absoluteCredit

		if (absoluteCredit > total || absoluteCredit === total) {
			amount = total
		} else if (absoluteCredit < total) {
			amount = absoluteCredit
		}

		return amount
	}, [availableCredit, totalCostWithPromo])

	const totalCostWithPromoAndHouseholdCredit = useMemo(() => {
		return totalCostWithPromo - householdCreditToApply
	}, [totalCostWithPromo, householdCreditToApply])

	return (
		<>
			<StyledDialog
				open={open}
				onClose={handleOnCancelled}
				title={title}
				showActivityIndicator={loading}
				minHeight={500}>
				<DialogContent style={{ display: 'flex' }}>
					<div className={classes.dialogContent}>
						<div className={classes.rowWrapper}>
							<div className={classes.row}>
								<div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
									<div>
										<ClientAutocomplete
											displayAddress={false}
											onHouseholdSelected={(household) => handleHouseholdSelected(household)}
											additionalSearchParams={{
												includePets: true,
												includeVaccines: true,
												includeAccounts: true,
												includeTraining: true
											}}
										/>
										{primaryAlreadyEnrolled ? (
											<StyledAlert
												style={{ marginTop: '.5rem' }}
												type="error"
												text={`${editedEnrollment.primary_handler.full_name} is already enrolled.`}></StyledAlert>
										) : null}
									</div>

									{editedEnrollment.otherHouseholdAccounts?.length > 0 ? (
										<div>
											<StyledSelect
												header="Additional Client (Optional)"
												label="Additional Client"
												required={true}
												value={editedEnrollment.secondary_handler || ''}
												onChange={(e) =>
													handleFieldUpdate({
														field: 'secondary_handler',
														targetValue: e.target.value
													})
												}>
												{editedEnrollment.otherHouseholdAccounts?.map((account, index) => {
													return (
														<MenuItem
															key={`secondary-handler-${index}`}
															value={account._id}>
															{account.full_name}
														</MenuItem>
													)
												})}
											</StyledSelect>
											{secondaryAlreadyEnrolled ? (
												<StyledAlert
													style={{ marginTop: '.5rem' }}
													type="error"
													text={`This additional client is already enrolled.`}></StyledAlert>
											) : null}
										</div>
									) : null}

									<CheckboxMenu
										header={editedEnrollment.secondary_handler ? 'Add Pet(s)*' : 'Add Pet*'}
										subheader={`Select which ${
											editedEnrollment.secondary_handler ? 'pet(s)' : 'pet'
										} to add from the list of existing pet profiles on this client's account`}
										maxSelections={
											primaryAlreadyEnrolled
												? editedEnrollment.secondary_handler
													? 1
													: 0
												: editedEnrollment.secondary_handler
												? 2
												: 1
										}
										options={petOptions}
										onSelectedChanged={(v) => {
											if (v && Array.isArray(v)) {
												const selectedPets =
													editedEnrollment.availableHouseholdPets.filter((pet) =>
														v.includes(pet._id)
													)
												handleFieldUpdate({
													field: 'pets',
													targetValue: selectedPets
												})
											}
										}}
									/>
									{error ? <StyledAlert type="error" text={error} /> : null}
								</div>
								<div>
									<div className={classes.summaryWrapper}>
										<Typography variant="h2">Summary</Typography>
										<div className={classes.summaryTableWrapper}>
											<table>
												<thead>
													<tr>
														<th>Item</th>
														<th>Qty</th>
														<th>Cost</th>
													</tr>
												</thead>
												<tbody>
													<tr>
														<td>Training Sessions</td>
														<td>{numberOfSessions}x</td>
														<td>
															{formatNumsToDollars(
																trainingClass?.price_per_session || 0
															)}
														</td>
													</tr>
													{appliedPromo ? (
														<tr>
															<td>{appliedPromo.code}</td>
															<td>{NON_BREAKING_SPACE}</td>
															<td>-{formatNumsToDollars(-promoAmountToApply)}</td>
														</tr>
													) : null}
													{householdCreditToApply > 0 ? (
														<tr>
															<td>Account Credit</td>
															<td>{NON_BREAKING_SPACE}</td>
															<td>-{formatNumsToDollars(householdCreditToApply)}</td>
														</tr>
													) : null}
												</tbody>
											</table>
										</div>
										<div className={classes.summaryTotalWrapper}>
											<div>Total</div>
											<div>{formatNumsToDollars(totalCostWithPromoAndHouseholdCredit)}</div>
										</div>
										<Button
											onClick={() =>
												appliedPromo
													? handleRemoveAppliedPromo()
													: handlePromoEntryDialogState()
											}
											style={{
												color: canAddPromo
													? appliedPromo
														? dangers[500]
														: '#6B4AD6'
													: gray[500],
												fontSize: 14,
												letterSpacing: 1.25
											}}
											disabled={!canAddPromo}
											variant="text">
											{appliedPromo ? 'Remove Promo' : 'Add Promo'}
										</Button>
									</div>
								</div>
							</div>
						</div>
						<div className={classes.dialogActions}>
							<MainButton
								label="Confirm"
								onClick={handleOnSave}
								variant="contained"
								disabled={!canSave}
							/>
							<MainButton label="Cancel" onClick={handleOnCancelled} />
						</div>
					</div>
				</DialogContent>
			</StyledDialog>
			<PromoOnlyEntryDialog
				open={promoEntryDialogOpen}
				apiAction={PROMO_API_ACTIONS.Validate}
				service_target={TRAINING}
				training_course_abbreviation={trainingClass?.training_course?.abbreviation}
				training_course_start_date={trainingClass?.startDate}
				household_id={editedEnrollment?.primary_handler?.household}
				onCancel={handlePromoEntryDialogState}
				onComplete={handleOnPromoValidationComplete}
			/>
		</>
	)
}

export default TrainingEnrollmentDialog
